import React, { useState, useEffect } from 'react';
import { 
  Calendar, Users, Clock, Plus, Pencil, Trash2, 
  CheckCircle2, AlertCircle, DollarSign, Settings,
  ChevronRight, Play, Pause, RefreshCw, UserPlus, UserMinus, Search, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import { Separator } from '../../ui/separator';
import { Checkbox } from '../../ui/checkbox';
import { ScrollArea } from '../../ui/scroll-area';
import { db } from '../../../lib/db';
import { PAY_FREQUENCIES } from '../../../lib/usPayrollCalculations';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

const DAYS_OF_WEEK = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' }
];

const DEFAULT_PAY_GROUP = {
  name: '',
  description: '',
  frequency: 'biweekly',
  payDay: 'friday',
  periodStartDay: 'sunday',
  cutoffDays: 3,
  processingDays: 2,
  directDepositLeadDays: 2,
  status: 'active'
};

export default function PayGroupManagement() {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [payGroups, setPayGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [compensation, setCompensation] = useState([]);
  
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_PAY_GROUP);
  
  // Employee assignment modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningGroup, setAssigningGroup] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      let [groups, users, compData] = await Promise.all([
        db.payGroups.toArray(),
        db.users.where('role').notEqual('admin').toArray(),
        db.employeeCompensation.toArray()
      ]);

      // Deduplicate pay groups by name (remove duplicates that may have been created)
      const seenGroups = new Set();
      groups = groups.filter(g => {
        if (seenGroups.has(g.name)) return false;
        seenGroups.add(g.name);
        return true;
      });

      setPayGroups(groups);
      setEmployees(users);
      setCompensation(compData);
    } catch (error) {
      console.error('Error loading pay groups:', error);
      toast.error('Failed to load pay groups');
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeCount = (groupId) => {
    return compensation.filter(c => c.payGroupId === groupId).length;
  };

  const getNextPayDate = (group) => {
    const today = new Date();
    const dayIndex = DAYS_OF_WEEK.findIndex(d => d.value === group.payDay);
    const daysUntilPayDay = (dayIndex - today.getDay() + 7) % 7 || 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilPayDay);
    return nextDate.toLocaleDateString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a pay group name');
      return;
    }

    try {
      if (isEditing && selectedGroup) {
        await db.payGroups.update(selectedGroup.id, {
          ...formData,
          updatedAt: Date.now()
        });
        toast.success('Pay group updated successfully');
      } else {
        await db.payGroups.add({
          id: uuidv4(),
          ...formData,
          createdAt: Date.now()
        });
        toast.success('Pay group created successfully');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving pay group:', error);
      toast.error('Failed to save pay group');
    }
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      frequency: group.frequency,
      payDay: group.payDay,
      periodStartDay: group.periodStartDay || 'sunday',
      cutoffDays: group.cutoffDays || 3,
      processingDays: group.processingDays || 2,
      directDepositLeadDays: group.directDepositLeadDays || 2,
      status: group.status
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (group) => {
    const empCount = getEmployeeCount(group.id);
    if (empCount > 0) {
      toast.error(`Cannot delete pay group with ${empCount} assigned employees`);
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Pay Group',
      description: `Delete pay group "${group.name}"?`,
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) {
      return;
    }

    try {
      await db.payGroups.delete(group.id);
      toast.success('Pay group deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting pay group:', error);
      toast.error('Failed to delete pay group');
    }
  };

  const toggleStatus = async (group) => {
    try {
      const newStatus = group.status === 'active' ? 'inactive' : 'active';
      await db.payGroups.update(group.id, { 
        status: newStatus,
        updatedAt: Date.now()
      });
      toast.success(`Pay group ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData(DEFAULT_PAY_GROUP);
    setSelectedGroup(null);
    setIsEditing(false);
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Employee assignment functions
  const getGroupEmployees = (groupId) => {
    return employees.filter(emp => 
      compensation.some(c => c.userId === emp.id && c.payGroupId === groupId)
    );
  };

  const getUnassignedEmployees = () => {
    return employees.filter(emp => 
      !compensation.some(c => c.userId === emp.id && c.payGroupId)
    );
  };

  const openAssignModal = (group) => {
    setAssigningGroup(group);
    setSelectedEmployees([]);
    setEmployeeSearch('');
    setShowAssignModal(true);
  };

  const handleAssignEmployees = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      for (const empId of selectedEmployees) {
        const existingComp = compensation.find(c => c.userId === empId);
        
        if (existingComp) {
          // Update existing compensation record
          await db.employeeCompensation.update(existingComp.id, {
            payGroupId: assigningGroup.id,
            payFrequency: assigningGroup.frequency,
            updatedAt: Date.now()
          });
        } else {
          // Create new compensation record with pay group
          await db.employeeCompensation.add({
            id: uuidv4(),
            userId: empId,
            payGroupId: assigningGroup.id,
            payFrequency: assigningGroup.frequency,
            payType: 'salary',
            payRate: 0, // User needs to set this in Employee Setup
            workState: '',
            residentState: '',
            flsaStatus: 'exempt',
            status: 'pending', // Mark as pending until pay rate is set
            createdAt: Date.now()
          });
        }
      }

      toast.success(`${selectedEmployees.length} employee(s) assigned to ${assigningGroup.name}`);
      setShowAssignModal(false);
      loadData();
    } catch (error) {
      console.error('Error assigning employees:', error);
      toast.error('Failed to assign employees');
    }
  };

  const handleRemoveFromGroup = async (empId, groupId) => {
    const confirmed = await confirm({
      title: 'Remove Employee',
      description: 'Remove this employee from the pay group?',
      confirmText: 'Remove',
      variant: 'destructive'
    });
    if (!confirmed) return;

    try {
      const comp = compensation.find(c => c.userId === empId && c.payGroupId === groupId);
      if (comp) {
        await db.employeeCompensation.update(comp.id, {
          payGroupId: null,
          updatedAt: Date.now()
        });
        toast.success('Employee removed from pay group');
        loadData();
      }
    } catch (error) {
      console.error('Error removing employee:', error);
      toast.error('Failed to remove employee');
    }
  };

  const filteredUnassignedEmployees = getUnassignedEmployees().filter(emp => {
    if (!employeeSearch) return true;
    const fullName = `${emp.profile?.firstName || ''} ${emp.profile?.lastName || ''}`.toLowerCase();
    return fullName.includes(employeeSearch.toLowerCase()) || 
           emp.email?.toLowerCase().includes(employeeSearch.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pay Group Management</h1>
          <p className="text-gray-500 mt-1">Configure pay schedules and frequencies</p>
        </div>
        <Button onClick={openNewModal}>
          <Plus className="w-4 h-4 mr-2" /> Add Pay Group
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Pay Groups</p>
                <p className="text-2xl font-bold">{payGroups.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Groups</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {payGroups.filter(g => g.status === 'active').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Employees</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unassigned</p>
                <p className="text-2xl font-bold text-amber-600">
                  {employees.length - compensation.filter(c => c.payGroupId).length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pay Groups List */}
      <Card>
        <CardHeader>
          <CardTitle>Pay Groups</CardTitle>
          <CardDescription>Manage employee pay schedules and cutoff dates</CardDescription>
        </CardHeader>
        <CardContent>
          {payGroups.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No pay groups configured</p>
              <Button onClick={openNewModal}>
                <Plus className="w-4 h-4 mr-2" /> Create First Pay Group
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pay Group</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Pay Day</TableHead>
                  <TableHead>Cutoff</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead>Next Pay Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payGroups.map(group => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{group.name}</p>
                        {group.description && (
                          <p className="text-xs text-gray-500">{group.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {PAY_FREQUENCIES[group.frequency]?.label || group.frequency}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{group.payDay}</TableCell>
                    <TableCell>{group.cutoffDays} days before</TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex items-center gap-1 hover:bg-blue-50"
                        onClick={() => openAssignModal(group)}
                      >
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{getEmployeeCount(group.id)}</span>
                        <UserPlus className="w-3 h-3 text-gray-400 ml-1" />
                      </Button>
                    </TableCell>
                    <TableCell>{getNextPayDate(group)}</TableCell>
                    <TableCell>
                      <Badge className={group.status === 'active' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-gray-100 text-gray-700'
                      }>
                        {group.status === 'active' ? (
                          <><Play className="w-3 h-3 mr-1" /> Active</>
                        ) : (
                          <><Pause className="w-3 h-3 mr-1" /> Inactive</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(group)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleStatus(group)}>
                          {group.status === 'active' ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(group)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Pay Group' : 'Add Pay Group'}</DialogTitle>
            <DialogDescription>
              Configure pay schedule settings for a group of employees
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Pay Group Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Bi-Weekly Salaried"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pay Frequency *</Label>
                <Select 
                  value={formData.frequency} 
                  onValueChange={(v) => setFormData({...formData, frequency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAY_FREQUENCIES).map(([key, freq]) => (
                      <SelectItem key={key} value={key}>{freq.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Pay Day *</Label>
                <Select 
                  value={formData.payDay} 
                  onValueChange={(v) => setFormData({...formData, payDay: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map(day => (
                      <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start Day</Label>
                <Select 
                  value={formData.periodStartDay} 
                  onValueChange={(v) => setFormData({...formData, periodStartDay: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map(day => (
                      <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Cutoff Days Before Pay</Label>
                <Input
                  type="number"
                  min="0"
                  max="14"
                  value={formData.cutoffDays}
                  onChange={(e) => setFormData({...formData, cutoffDays: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Processing Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="7"
                  value={formData.processingDays}
                  onChange={(e) => setFormData({...formData, processingDays: parseInt(e.target.value)})}
                />
                <p className="text-xs text-gray-500">Days needed to process payroll</p>
              </div>

              <div className="space-y-2">
                <Label>Direct Deposit Lead Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.directDepositLeadDays}
                  onChange={(e) => setFormData({...formData, directDepositLeadDays: parseInt(e.target.value)})}
                />
                <p className="text-xs text-gray-500">ACH file submission lead time</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) => 
                    setFormData({...formData, status: checked ? 'active' : 'inactive'})
                  }
                />
                <Label>Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditing ? 'Update Pay Group' : 'Create Pay Group'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Assignment Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Manage Employees - {assigningGroup?.name}
            </DialogTitle>
            <DialogDescription>
              Assign or remove employees from this pay group
            </DialogDescription>
          </DialogHeader>

          {assigningGroup && (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Current Employees in Group */}
              <div className="flex-1 min-h-0">
                <Label className="text-sm font-medium mb-2 block">
                  Current Employees ({getEmployeeCount(assigningGroup.id)})
                </Label>
                <ScrollArea className="h-40 border rounded-lg">
                  {getGroupEmployees(assigningGroup.id).length === 0 ? (
                    <p className="text-sm text-gray-500 p-4 text-center">No employees assigned yet</p>
                  ) : (
                    <div className="p-2 space-y-1">
                      {getGroupEmployees(assigningGroup.id).map(emp => {
                        const comp = compensation.find(c => c.userId === emp.id);
                        return (
                          <div 
                            key={emp.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-sm">
                                {(emp.profile?.firstName?.[0] || '') + (emp.profile?.lastName?.[0] || '')}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {emp.profile?.firstName} {emp.profile?.lastName}
                                </p>
                                <p className="text-xs text-gray-500">{emp.profile?.department || 'No department'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {comp?.payRate > 0 ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Configured</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Needs Setup</Badge>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRemoveFromGroup(emp.id, assigningGroup.id)}
                              >
                                <UserMinus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </div>

              <Separator />

              {/* Add Unassigned Employees */}
              <div className="flex-1 min-h-0">
                <Label className="text-sm font-medium mb-2 block">
                  Add Employees ({getUnassignedEmployees().length} unassigned)
                </Label>
                
                <div className="relative mb-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input 
                    placeholder="Search employees..."
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="pl-9"
                  />
                  {employeeSearch && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setEmployeeSearch('')}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>

                <ScrollArea className="h-40 border rounded-lg">
                  {filteredUnassignedEmployees.length === 0 ? (
                    <p className="text-sm text-gray-500 p-4 text-center">
                      {getUnassignedEmployees().length === 0 
                        ? 'All employees are assigned to pay groups' 
                        : 'No matching employees found'}
                    </p>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredUnassignedEmployees.map(emp => (
                        <div 
                          key={emp.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedEmployees.includes(emp.id) 
                              ? 'bg-blue-50 border border-blue-200' 
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setSelectedEmployees(prev => 
                              prev.includes(emp.id)
                                ? prev.filter(id => id !== emp.id)
                                : [...prev, emp.id]
                            );
                          }}
                        >
                          <Checkbox 
                            checked={selectedEmployees.includes(emp.id)}
                            onCheckedChange={(checked) => {
                              setSelectedEmployees(prev => 
                                checked 
                                  ? [...prev, emp.id]
                                  : prev.filter(id => id !== emp.id)
                              );
                            }}
                          />
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-sm">
                            {(emp.profile?.firstName?.[0] || '') + (emp.profile?.lastName?.[0] || '')}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">
                              {emp.profile?.firstName} {emp.profile?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {emp.profile?.department || 'No department'} • {emp.email}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                {selectedEmployees.length > 0 && (
                  <p className="text-sm text-blue-600 mt-2">
                    {selectedEmployees.length} employee(s) selected
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Close
            </Button>
            {selectedEmployees.length > 0 && (
              <Button onClick={handleAssignEmployees}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add {selectedEmployees.length} Employee(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
